import React, { useState } from 'react';
import { X, RefreshCw, ChevronRight, ChevronLeft, Info, Calculator, DollarSign, Layers } from 'lucide-react';

const TIPOS = [
  {
    id: 'LEGAL',
    label: 'Legal',
    subtitle: '10% utilidad neta',
    desc: 'Distribución proporcional al salario y tiempo trabajado con topes legales (45/60 días).',
    color: 'border-blue-200 bg-blue-50',
    activeColor: 'border-blue-500 bg-blue-50',
    icon: Calculator,
    iconColor: 'text-blue-600',
  },
  {
    id: 'FIJA',
    label: 'Fija',
    subtitle: 'Monto definido',
    desc: 'Bono de monto global, por salario o por empleado con distribución configurable.',
    color: 'border-violet-200 bg-violet-50',
    activeColor: 'border-violet-500 bg-violet-50',
    icon: DollarSign,
    iconColor: 'text-violet-600',
  },
  {
    id: 'MIXTA',
    label: 'Mixta',
    subtitle: 'Legal con distribución',
    desc: 'Paga el tope legal si el fondo lo cubre; de lo contrario distribuye el fondo proporcionalmente.',
    color: 'border-teal-200 bg-teal-50',
    activeColor: 'border-teal-500 bg-teal-50',
    icon: Layers,
    iconColor: 'text-teal-600',
  },
];

const fmt2 = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CrearBonificacionModal({ onClose, onConfirm, calculating }) {
  const currentYear = new Date().getFullYear();
  const [step, setStep] = useState(1); // 1: tipo, 2: params, 3: confirm

  const [form, setForm] = useState({
    anio: currentYear - 1,
    nombre: '',
    tipo: '',
    fecha_pago_prevista: '',
    // LEGAL
    porcentaje_utilidad: 10,
    utilidad_neta: '',
    aplicar_topes: true,
    // FIJA / MIXTA adicional
    metodo_calculo: 'monto_global',
    metodo_distribucion: 'proporcional',
    monto_global: '',
    cantidad_salarios: '',
    monto_por_empleado: '',
    // General
    incluir_inactivos: false,
    observaciones: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const montoDistribuir = parseFloat(form.utilidad_neta || 0) * (parseFloat(form.porcentaje_utilidad || 10) / 100);

  const canNext1 = !!form.tipo;
  const canNext2 = () => {
    if (!form.nombre.trim()) return false;
    if (!form.fecha_pago_prevista) return false;
    if (form.tipo === 'LEGAL' || form.tipo === 'MIXTA') {
      if (!form.utilidad_neta || parseFloat(form.utilidad_neta) <= 0) return false;
    }
    if (form.tipo === 'FIJA') {
      if (form.metodo_calculo === 'antiguedad') { /* no requiere montos */ }
      else if (form.metodo_calculo === 'monto_global' && (!form.monto_global || parseFloat(form.monto_global) <= 0)) return false;
      else if (form.metodo_calculo === 'cantidad_salarios' && (!form.cantidad_salarios || parseFloat(form.cantidad_salarios) <= 0)) return false;
      else if (form.metodo_calculo === 'monto_por_empleado' && (!form.monto_por_empleado || parseFloat(form.monto_por_empleado) <= 0)) return false;
    }
    return true;
  };

  const periodoLabel = form.fecha_pago_prevista
    ? new Date(form.fecha_pago_prevista + 'T00:00:00').toLocaleDateString('es-DO', { month: 'long', year: 'numeric' })
    : '';

  const handleSubmit = () => {
    onConfirm({
      anio: parseInt(form.anio),
      nombre: form.nombre.trim(),
      tipo: form.tipo,
      fecha_pago_prevista: form.fecha_pago_prevista,
      porcentaje_utilidad: parseFloat(form.porcentaje_utilidad || 10),
      utilidad_neta: parseFloat(form.utilidad_neta || 0),
      aplicar_topes: form.aplicar_topes,
      metodo_calculo: form.metodo_calculo || null,
      metodo_distribucion: form.metodo_distribucion || null,
      monto_global: parseFloat(form.monto_global || 0),
      cantidad_salarios: parseFloat(form.cantidad_salarios || 0),
      monto_por_empleado: parseFloat(form.monto_por_empleado || 0),
      incluir_inactivos: form.incluir_inactivos,
      observaciones: form.observaciones || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-bold text-gray-900">Nueva Bonificación Anual</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Paso {step} de 3 · {step === 1 ? 'Tipo de bonificación' : step === 2 ? 'Configuración' : 'Confirmación'}
            </p>
          </div>
          <button onClick={onClose} disabled={calculating} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-5">

          {/* ── STEP 1: Tipo ───────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">Selecciona el método de cálculo de la bonificación:</p>
              {TIPOS.map(t => {
                const active = form.tipo === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => set('tipo', t.id)}
                    className={`w-full text-left border-2 rounded-xl p-4 transition-all ${active ? t.activeColor + ' ring-1 ring-offset-0' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 p-1.5 rounded-lg ${active ? 'bg-white bg-opacity-70' : 'bg-gray-100'}`}>
                        <t.icon size={16} className={active ? t.iconColor : 'text-gray-500'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">{t.label}</p>
                          <span className="text-xs text-gray-500">{t.subtitle}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
                      </div>
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 ${active ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                        {active && <div className="w-full h-full rounded-full bg-white scale-50 block" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── STEP 2: Parámetros ─────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-4">

              {/* Nombre y año */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={e => set('nombre', e.target.value)}
                    placeholder={`Bonificación ${form.tipo} ${form.anio}`}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Año Fiscal</label>
                  <input
                    type="number"
                    value={form.anio}
                    onChange={e => set('anio', parseInt(e.target.value, 10))}
                    min={2020} max={currentYear}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                  />
                </div>
              </div>

              {/* Fecha de pago — input del motor */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
                <div>
                  <label className="block text-xs font-bold text-blue-800 mb-1.5">
                    Fecha prevista de pago <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.fecha_pago_prevista}
                    onChange={e => set('fecha_pago_prevista', e.target.value)}
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white"
                  />
                </div>
                {periodoLabel ? (
                  <p className="text-xs text-blue-700">
                    El ISR retenido se registrará en <span className="font-bold">{periodoLabel}</span> y el motor ISR lo balanceará con la nómina regular de ese mes.
                  </p>
                ) : (
                  <p className="text-xs text-blue-500">Define cuándo se pagará esta bonificación. Esto determina el período ISR y no podrá cambiarse sin recalcular.</p>
                )}
              </div>

              {/* Sección LEGAL */}
              {(form.tipo === 'LEGAL' || form.tipo === 'MIXTA') && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-xs font-bold text-blue-800 uppercase tracking-wide">Componente Legal</p>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">% Utilidad</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={form.porcentaje_utilidad}
                          onChange={e => set('porcentaje_utilidad', e.target.value)}
                          min={1} max={100} step={0.5}
                          className="w-full px-3 py-2 pr-7 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Utilidad Neta Anual <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={form.utilidad_neta}
                        onChange={e => set('utilidad_neta', e.target.value)}
                        min={0} step={1000}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                      />
                    </div>
                  </div>

                  {parseFloat(form.utilidad_neta) > 0 && (
                    <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-blue-200">
                      <span className="text-xs text-gray-500">Monto a distribuir ({form.porcentaje_utilidad}%)</span>
                      <span className="text-sm font-bold text-blue-700">RD$ {fmt2(montoDistribuir)}</span>
                    </div>
                  )}

                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.aplicar_topes}
                      onChange={e => set('aplicar_topes', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300"
                    />
                    <div>
                      <p className="text-xs font-medium text-gray-700">Aplicar topes legales</p>
                      <p className="text-xs text-gray-400">≤3 años: 45 días · &gt;3 años: 60 días de salario</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Sección FIJA */}
              {form.tipo === 'FIJA' && (
                <div className="space-y-3 p-4 bg-violet-50 border border-violet-200 rounded-xl">
                  <p className="text-xs font-bold text-violet-800 uppercase tracking-wide">
                    Configuración del Bono
                  </p>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Método de cálculo</label>
                    <select
                      value={form.metodo_calculo}
                      onChange={e => set('metodo_calculo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                    >
                      <option value="antiguedad">45/60 Días por Antigüedad</option>
                      <option value="monto_global">Monto Global</option>
                      <option value="cantidad_salarios">N × Salario mensual</option>
                      <option value="monto_por_empleado">Monto fijo por empleado</option>
                    </select>
                  </div>

                  {form.metodo_calculo === 'antiguedad' && (
                    <div className="flex items-start gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2.5">
                      <Info size={13} className="text-violet-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-violet-700 space-y-0.5">
                        <p className="font-semibold">Cálculo directo por antigüedad</p>
                        <p>· <strong>&lt; 3 años</strong>: (salario promedio × meses/12) / 23.83 × <strong>45 días</strong></p>
                        <p>· <strong>≥ 3 años</strong>: (salario promedio × meses/12) / 23.83 × <strong>60 días</strong></p>
                        <p className="text-violet-500 mt-0.5">Salario promedio = base + comisiones + incentivos si aplica, de las nóminas procesadas del año.</p>
                      </div>
                    </div>
                  )}

                  {form.metodo_calculo === 'monto_global' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          Monto Global <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={form.monto_global}
                          onChange={e => set('monto_global', e.target.value)}
                          min={0} step={1000} placeholder="0.00"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Distribución</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { id: 'proporcional', label: 'Proporcional', sub: 'según salario' },
                            { id: 'igualitario',  label: 'Igualitario',  sub: 'partes iguales' },
                            { id: 'desempeno',    label: 'Desempeño',    sub: 'por puntaje' },
                          ].map(opt => (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => set('metodo_distribucion', opt.id)}
                              className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                                form.metodo_distribucion === opt.id
                                  ? 'border-violet-500 bg-violet-100'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <p className="text-xs font-semibold text-gray-800">{opt.label}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{opt.sub}</p>
                            </button>
                          ))}
                        </div>
                        {form.metodo_distribucion === 'desempeno' && (
                          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                            <Info size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-700">Los puntajes de desempeño se ingresan por empleado después de generar el borrador.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {form.metodo_calculo === 'cantidad_salarios' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Cantidad de salarios <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={form.cantidad_salarios}
                        onChange={e => set('cantidad_salarios', e.target.value)}
                        min={0} step={0.25} placeholder="ej. 1, 1.5, 2"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                      />
                      <p className="text-xs text-gray-400 mt-1">Cada empleado recibirá este múltiplo de su salario mensual.</p>
                    </div>
                  )}

                  {form.metodo_calculo === 'monto_por_empleado' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Monto por empleado <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={form.monto_por_empleado}
                        onChange={e => set('monto_por_empleado', e.target.value)}
                        min={0} step={500} placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Config general */}
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.incluir_inactivos}
                  onChange={e => set('incluir_inactivos', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                />
                <div>
                  <p className="text-xs font-medium text-gray-700">Incluir empleados inactivos</p>
                  <p className="text-xs text-gray-400">Por defecto solo se incluyen empleados activos con contrato vigente.</p>
                </div>
              </label>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
                <textarea
                  value={form.observaciones}
                  onChange={e => set('observaciones', e.target.value)}
                  rows={2}
                  placeholder="Notas internas opcionales..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 resize-none"
                />
              </div>
            </div>
          )}

          {/* ── STEP 3: Confirmación ───────────────────────────── */}
          {step === 3 && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">Revisa la configuración antes de calcular:</p>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2 text-sm">
                <Row label="Nombre"          value={form.nombre} />
                <Row label="Año fiscal"      value={form.anio} />
                <Row label="Tipo"            value={form.tipo} />
                <Row label="Fecha de pago"   value={periodoLabel} bold />
                {(form.tipo === 'LEGAL' || form.tipo === 'MIXTA') && (
                  <>
                    <Row label="Utilidad neta"       value={`RD$ ${fmt2(form.utilidad_neta)}`} />
                    <Row label="% Utilidad"          value={`${form.porcentaje_utilidad}%`} />
                    <Row label="Monto a distribuir"  value={`RD$ ${fmt2(montoDistribuir)}`} bold />
                    <Row label="Topes legales"       value={form.aplicar_topes ? 'Sí' : 'No'} />
                  </>
                )}
                {form.tipo === 'FIJA' && (
                  <>
                    <Row label="Método"      value={
                      form.metodo_calculo === 'antiguedad'         ? '45/60 Días por Antigüedad' :
                      form.metodo_calculo === 'monto_global'       ? 'Monto Global' :
                      form.metodo_calculo === 'cantidad_salarios'  ? `${form.cantidad_salarios} salario(s)` :
                      `RD$ ${fmt2(form.monto_por_empleado)} / empleado`
                    } />
                    {form.metodo_calculo === 'monto_global' && (
                      <>
                        <Row label="Monto global"   value={`RD$ ${fmt2(form.monto_global)}`} bold />
                        <Row label="Distribución"   value={form.metodo_distribucion} />
                      </>
                    )}
                  </>
                )}
                <Row label="Incluye inactivos" value={form.incluir_inactivos ? 'Sí' : 'No'} />
              </div>

              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  Si ya existe un borrador del mismo tipo y año, será reemplazado. Los registros aprobados o pagados no se pueden regenerar.
                </p>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex gap-3 sticky bottom-0 bg-white">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep(s => s - 1)}
              disabled={calculating}
              className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft size={14} /> Atrás
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={calculating}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <div className="flex-1" />
          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep(s => s + 1)}
              disabled={step === 1 ? !canNext1 : !canNext2()}
              className="flex items-center gap-1.5 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              Siguiente <ChevronRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={calculating}
              className="flex items-center justify-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {calculating ? (
                <><RefreshCw size={14} className="animate-spin" /> Calculando…</>
              ) : 'Calcular Bonificación'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500 text-xs">{label}</span>
      <span className={`text-xs ${bold ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>{value}</span>
    </div>
  );
}
